import type { RouterHistory } from '@tanstack/react-router';

type HistoryLocation = RouterHistory['location'];
type NavigationHistory = Pick<RouterHistory, 'location' | 'subscribe'>;
type NavigationRecord = {
  currentIndex: number;
  maxIndex: number;
  currentKey: string;
  historyLength: number;
};
export type DesktopNavigationState = { canGoBack: boolean; canGoForward: boolean };
type Persistence = {
  read: () => unknown;
  write: (record: NavigationRecord) => void;
  length: () => number;
};

function indexOf(location: HistoryLocation) {
  const index = location.state.__TSR_index;
  return Number.isInteger(index) && index >= 0 ? index : 0;
}
function keyOf(location: HistoryLocation) {
  return location.state.__TSR_key || location.state.key || '';
}

/** TanStack exposes back availability but not the retained forward branch. */
export function trackDesktopNavigation(history: NavigationHistory, persistence?: Persistence) {
  let currentIndex = indexOf(history.location);
  let maxIndex = currentIndex;
  try {
    const saved = persistence?.read() as Partial<NavigationRecord> | undefined;
    if (
      saved &&
      saved.currentIndex === currentIndex &&
      keyOf(history.location) &&
      saved.currentKey === keyOf(history.location) &&
      saved.historyLength === persistence?.length() &&
      Number.isInteger(saved.maxIndex) &&
      saved.maxIndex! >= currentIndex
    )
      maxIndex = saved.maxIndex!;
  } catch {
    /* Unavailable storage does not disable navigation during this visit. */
  }
  let snapshot: DesktopNavigationState = {
    canGoBack: currentIndex > 0,
    canGoForward: currentIndex < maxIndex,
  };
  const listeners = new Set<() => void>();
  let queued = false;
  const persist = () => {
    if (!persistence || queued) return;
    queued = true;
    queueMicrotask(() => {
      queued = false;
      try {
        persistence.write({
          currentIndex,
          maxIndex,
          currentKey: keyOf(history.location),
          historyLength: persistence.length(),
        });
      } catch {
        /* Optional session metadata. */
      }
    });
  };
  const stop = history.subscribe(({ location, action }) => {
    currentIndex = indexOf(location);
    // A new visit discards the old forward branch. Replacing a visit does not.
    maxIndex = action.type === 'PUSH' ? currentIndex : Math.max(currentIndex, maxIndex);
    const next = { canGoBack: currentIndex > 0, canGoForward: currentIndex < maxIndex };
    if (next.canGoBack !== snapshot.canGoBack || next.canGoForward !== snapshot.canGoForward) {
      snapshot = next;
      listeners.forEach((listener) => listener());
    }
    persist();
  });
  persist();
  return {
    getSnapshot: () => snapshot,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    dispose: stop,
  };
}

const trackers = new WeakMap<RouterHistory, ReturnType<typeof trackDesktopNavigation>>();
const storageKey = 'kiipu.desktop.navigation.v1';
/** The history owner outlives individual Library/search page mounts. */
export function desktopNavigationTracker(history: RouterHistory) {
  let tracker = trackers.get(history);
  if (!tracker) {
    tracker = trackDesktopNavigation(history, {
      read: () => JSON.parse(sessionStorage.getItem(storageKey) || 'null'),
      write: (record) => sessionStorage.setItem(storageKey, JSON.stringify(record)),
      length: () => window.history.length,
    });
    trackers.set(history, tracker);
  }
  return tracker;
}
